//return black or white based on the best contrast for a given colorId
export function colorContrastCSS(colorId: string, opacity = 1): string {
    return colorIsDark(colorId)
        ? colorCSS("N0", opacity)
        : colorCSS("N900", opacity);
}

//return the color with the most contrast for a given colorId
export function headingColorContrastCSS(colorId: string, opacity = 1): string {
    if (
        colorIsDark(colorId) &&
        !colorId.startsWith("N") &&
        colorId !== "trelloblue"
    ) {
        return colorCSS(`${colorId[0]}50`, opacity);
    } else if (!colorIsDark(colorId) && !colorId.startsWith("N")) {
        return colorCSS(`${colorId[0]}500`, opacity);
    } else {
        return colorContrastCSS(colorId);
    }
}

//return the color with a darker shade for a given colorId
export function borderColorCSS(colorId: string, opacity = 0.5): string {
    if (colorId === "trelloblue") return colorCSS("B100", opacity);
    return colorCSS(`${colorId[0]}100`, opacity);
}

//output string CSS for a given id
export function colorCSS(colorId?: string, opacity = 1): string {
    if (colorId && colorExists(colorId)) {
        const [red, green, blue] = colors[colorId].rgb;
        return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
    } else {
        return "transparent";
    }
}

//for SVGs urlencoded in CSS
export function colorCSSforSVG(colorId?: string): string {
    if (!colorId || !colorExists(colorId)) {
        return "currentColor";
    }
    return encodeURIComponent(colorHex(colorId));
}

//return color in hex format
export function colorHex(colorId: ColorId): string {
    function hex(c: number) {
        const hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }

    const [red, green, blue] = colors[colorId].rgb;
    return `#${hex(red)}${hex(green)}${hex(blue)}`;
}

//determine whether string represents a defined color
export function colorExists(colorId?: string): colorId is ColorId {
    return typeof colorId === "string" && Object.keys(colors).includes(colorId);
}

//whether a color is dark
export function colorIsDark(colorId: string): boolean {
    return colorExists(colorId) && colors[colorId].isDark;
}

export type ColorId = keyof typeof colors;

//ADG3 + Trello color definitions
export const colors = {
    trelloblue: { rgb: [0, 121, 191], isDark: true },
    B50: { rgb: [222, 235, 255], isDark: false },
    B75: { rgb: [178, 212, 255], isDark: false },
    B100: { rgb: [76, 154, 255], isDark: false },
    B200: { rgb: [38, 132, 255], isDark: false },
    B300: { rgb: [0, 101, 255], isDark: true },
    B400: { rgb: [0, 82, 204], isDark: true },
    B500: { rgb: [7, 71, 166], isDark: true },
    G50: { rgb: [227, 252, 239], isDark: false },
    G75: { rgb: [171, 245, 209], isDark: false },
    G100: { rgb: [121, 242, 192], isDark: false },
    G200: { rgb: [87, 217, 163], isDark: false },
    G300: { rgb: [54, 179, 126], isDark: false },
    G400: { rgb: [0, 135, 90], isDark: true },
    G500: { rgb: [0, 102, 68], isDark: true },
    M50: { rgb: [255, 242, 251], isDark: false },
    M75: { rgb: [252, 198, 235], isDark: false },
    M100: { rgb: [249, 156, 219], isDark: false },
    M200: { rgb: [237, 80, 180], isDark: false },
    M300: { rgb: [214, 38, 136], isDark: true },
    M400: { rgb: [178, 40, 101], isDark: true },
    M500: { rgb: [134, 36, 67], isDark: true },
    N0: { rgb: [255, 255, 255], isDark: false },
    N10: { rgb: [250, 251, 252], isDark: false },
    N20: { rgb: [244, 245, 247], isDark: false },
    N30: { rgb: [235, 236, 250], isDark: false },
    N40: { rgb: [223, 225, 230], isDark: false },
    N50: { rgb: [193, 199, 208], isDark: false },
    N60: { rgb: [179, 186, 197], isDark: false },
    N70: { rgb: [165, 173, 186], isDark: false },
    N80: { rgb: [151, 160, 175], isDark: false },
    N90: { rgb: [137, 147, 164], isDark: false },
    N100: { rgb: [122, 134, 154], isDark: false },
    N200: { rgb: [108, 121, 143], isDark: true },
    N300: { rgb: [94, 108, 132], isDark: true },
    N400: { rgb: [80, 95, 121], isDark: true },
    N500: { rgb: [66, 82, 110], isDark: true },
    N600: { rgb: [52, 69, 99], isDark: true },
    N700: { rgb: [37, 56, 88], isDark: true },
    N800: { rgb: [23, 43, 77], isDark: true },
    N900: { rgb: [9, 30, 66], isDark: true },
    P50: { rgb: [234, 230, 255], isDark: false },
    P75: { rgb: [192, 182, 242], isDark: false },
    P100: { rgb: [153, 141, 217], isDark: false },
    P200: { rgb: [135, 119, 217], isDark: false },
    P300: { rgb: [101, 84, 192], isDark: true },
    P400: { rgb: [82, 67, 170], isDark: true },
    P500: { rgb: [64, 50, 148], isDark: true },
    R50: { rgb: [255, 235, 229], isDark: false },
    R75: { rgb: [255, 189, 173], isDark: false },
    R100: { rgb: [255, 143, 115], isDark: false },
    R200: { rgb: [255, 116, 82], isDark: false },
    R300: { rgb: [255, 86, 48], isDark: false },
    R400: { rgb: [222, 53, 11], isDark: true },
    R500: { rgb: [191, 38, 0], isDark: true },
    T50: { rgb: [230, 252, 255], isDark: false },
    T75: { rgb: [179, 245, 255], isDark: false },
    T100: { rgb: [121, 226, 242], isDark: false },
    T200: { rgb: [0, 199, 229], isDark: false },
    T300: { rgb: [0, 184, 217], isDark: false },
    T400: { rgb: [0, 163, 191], isDark: false },
    T500: { rgb: [0, 141, 166], isDark: true },
    Y50: { rgb: [255, 250, 229], isDark: false },
    Y75: { rgb: [255, 240, 178], isDark: false },
    Y100: { rgb: [255, 227, 128], isDark: false },
    Y200: { rgb: [255, 196, 0], isDark: false },
    Y300: { rgb: [255, 171, 0], isDark: false },
    Y400: { rgb: [255, 153, 31], isDark: false },
    Y500: { rgb: [255, 139, 0], isDark: false }
} as const;
